import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { PhoneIcon } from '@rsos/assets/icons';
import { Box, Button, Text } from '@rsos/components/capstone/base';
import { useSelectedTextFrom911Conversation } from '@rsos/reduxDataStore/src/features/textFrom911/textFrom911Selectors';
import { TF911_CONVERSATION_ENDED } from '@rsos/reduxDataStore/src/features/textFrom911/textFrom911Slice';
import useDisplayPhoneNumber from '../../../../hooks/useDisplayPhoneNumber';
import { HeaderContainer, RedCircle } from './TF911.styles';
import { TF911_CHAT_HEADER_HEIGHT } from './TF911ChatBox';
import TF911EndChat from './TF911EndChat';
import TF911ParticipantsPopover from './TF911ParticipantsPopover';
import TF911StartChatAccordion from './TF911StartChatAccordion';

/**
 * Displays a chat header above the chat box.
 * @param {Array} callChatParticipants - list of chat participants for 911 calls
 * @param {Boolean} isChatBoxVisible - Flag for expanding/collapsing the chat
 * @param {Function} onActionButtonClick - Onclick handler for toggling the
 * isTextFrom911ChatBoxVisible flag
 * @param {Function} onClickShowAccordion - Onclick handler for toggling the accordion for preconfigured messages
 * @param {Function} onStartConversation - Onclick handler for starting the conversation
 * @param {Boolean} showStartChat - Flag for showing the Start Chat button
 * @param {Boolean} showPreconfiguredMessages - Flag for showing preconfigured messages to start the chat with
 */
const TF911ChatHeader = ({
  callParticipants = [],
  isChatBoxVisible,
  onActionButtonClick,
  onClickShowAccordion,
  onStartConversation,
  showStartChat,
  showPreconfiguredMessages,
}) => {
  const selectedCallerID = useSelector(
    state => state.emergencyCalls.selectedCallerID,
  );

  const displayPhoneNumber = useDisplayPhoneNumber(selectedCallerID);

  const conversation = useSelectedTextFrom911Conversation(selectedCallerID);

  const isFetchingMetadata = useMemo(
    () => conversation?.loading?.getConversationMetadata,
    [conversation],
  );

  const isCreatingConversation = useMemo(
    () => conversation?.loading?.createConversation,
    [conversation],
  );

  const isConversationEnded = useMemo(
    () => conversation?.data?.custom?.STATUS === TF911_CONVERSATION_ENDED,
    [conversation],
  );

  return (
    <HeaderContainer
      data-name="text-from-911-chat-header"
      height={showPreconfiguredMessages ? '100%' : 'auto'}
      maxHeight={showStartChat ? '100%' : `${TF911_CHAT_HEADER_HEIGHT}px`}
    >
      <Box justify="space-between" width="100%" horizontal px="12px">
        <Box>
          <Text inline fontWeight="bold">
            <Text color="primaryText" size="normal">
              {!isFetchingMetadata &&
                conversation?.data?.conversation_id &&
                !isConversationEnded && <RedCircle />}
              {!isFetchingMetadata &&
                (!isConversationEnded ? 'Live ' : 'Closed ')}
              Chat
            </Text>
            <Box horizontal>
              <Text color="secondaryText">
                <PhoneIcon
                  width={10}
                  height={12}
                  viewboxWidth={11}
                  viewboxHeight={15}
                />
              </Text>
              <Text color="secondaryText" size="small">
                {displayPhoneNumber}
              </Text>
            </Box>
          </Text>
        </Box>

        <TF911ParticipantsPopover
          showStartChat={showStartChat}
          participants={callParticipants}
          isChatBoxVisible={isChatBoxVisible}
          onActionButtonClick={onActionButtonClick}
        />
      </Box>
      {isFetchingMetadata ? (
        <Box mt="8px" width="100%" height="100%" px="12px" pb="16px">
          <Box
            alignSelf="center"
            horizontal
            width="100%"
            borderRadius="0 0 5px 5px"
          >
            <Button
              btnType="tertiary"
              disabled={isFetchingMetadata}
              isLoadingProp={isFetchingMetadata}
              noMargin
              size="med"
              grow
              width="100%"
            />
          </Box>
        </Box>
      ) : showStartChat ? (
        <TF911StartChatAccordion
          onClickShowAccordion={onClickShowAccordion}
          onStartConversation={onStartConversation}
          showPreconfiguredMessages={showPreconfiguredMessages}
          showStartChat={showStartChat}
          isCreatingConversation={isCreatingConversation}
        />
      ) : (
        <TF911EndChat
          isChatBoxVisible={isChatBoxVisible}
          callerID={selectedCallerID}
        />
      )}
    </HeaderContainer>
  );
};

TF911ChatHeader.propTypes = {
  callParticipants: PropTypes.array,
  isChatBoxVisible: PropTypes.bool,
  onActionButtonClick: PropTypes.func,
  onClickShowAccordion: PropTypes.func,
  onEndConversation: PropTypes.func,
  onStartConversation: PropTypes.func,
  showStartChat: PropTypes.bool,
  showPreconfiguredMessages: PropTypes.bool,
  toggleChatBoxVisibility: PropTypes.func,
};

export default TF911ChatHeader;
